import { Component, Inject, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';

import { IMachine, IMaterialDto, IToolingListItem, MacroKind } from 'src/app/core/dto';
import { ClientDataStore } from '../../shared/services/clientData.store';
import { machineHasClaim } from '../../shared/services/store/machines/filter';
import { ToolingService } from '../../shared/services/tooling.service';
import { UnitsService } from '../../shared/services/units.service';
import { FlatViewVisualizerComponent } from '../flat-view-visualizer/flat-view-visualizer.component';
import { initialSelections, readLauncherMemory, writeLauncherMemory } from './launcher-memory';

/**
 * Ad-hoc entry into the flat-view/3D visualization: instead of a solution row pinning the
 * inputs, the user supplies them — machine, tooling code, material, and length. The material
 * stands in for the two values the solve derives from it (gauge and strip width), exactly as
 * SolutionWorkIndex does, so the picture matches what a solve with that material would use.
 *
 * The dialog stays open underneath the visualizer so the inputs can be tweaked and re-run —
 * it doubles as a what-if tool for tooling/material combinations before a job exists.
 *
 * Every field except patternId/patternName is an optional prefill: a future order-line entry
 * point knows the tooling code, material, and length already and only needs the machine asked.
 * Fields left empty start from the last run, on any pattern (launcher-memory.ts).
 */
export interface IFlatViewLauncherData {
  patternId: string;
  patternName: string;
  machineNumber?: number | null;
  toolingCode?: string | null;
  materialCode?: string | null;
  lengthIn?: number | null;
  /**
   * Set when the pattern is a macro, which is drawn as one call to it. A placed macro takes its
   * Y from the profile, so it needs a tooling code.
   */
  macroKind?: MacroKind | null;
  /** A starting length used only when neither the caller nor the last run supplies one. */
  suggestedLengthIn?: number | null;
}

// Keep the autocomplete dropdown responsive with large material catalogs.
const MAX_MATERIAL_OPTIONS = 50;

@Component({
  selector: 'app-flat-view-launcher',
  templateUrl: './flat-view-launcher.component.html',
  styleUrls: ['./flat-view-launcher.component.scss'],
  imports: [
    FormsModule,
    MatDialogModule,
    MatToolbarModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatAutocompleteModule,
    MatTooltipModule,
  ],
})
export class FlatViewLauncherComponent implements OnDestroy {
  machines: IMachine[] = [];
  machineNumber: number | null = null;
  toolingCode = '';
  materialText = '';
  lengthText = '';

  filteredMaterials: IMaterialDto[] = [];
  filteredToolings: IToolingListItem[] = [];
  private materials: IMaterialDto[] = [];
  private toolings: IToolingListItem[] = [];
  private machinesSub_: Subscription;
  private materialsSub_: Subscription;
  // The machine of the last run: preferred when a tooling code picks the machine.
  private lastMachine: number | null;

  constructor(
    public dialogRef: MatDialogRef<FlatViewLauncherComponent>,
    @Inject(MAT_DIALOG_DATA) public data: IFlatViewLauncherData,
    private clientDataStore: ClientDataStore,
    private toolingService: ToolingService,
    private unitsService: UnitsService,
    private dialog: MatDialog
  ) {
    const memory = readLauncherMemory();
    this.lastMachine = typeof memory.machineNumber === 'number' ? memory.machineNumber : null;
    const start = initialSelections(data, memory);
    this.machineNumber = start.machineNumber;
    this.toolingCode = start.toolingCode;
    this.materialText = start.materialText;
    this.lengthText = start.lengthText;

    this.machinesSub_ = this.clientDataStore.SelectMachines().subscribe((machines) => {
      this.machines = (machines ?? [])
        .filter(
          machineHasClaim('pro.machine.shape-punching-simple pro.machine.shape-punching-advanced')
        )
        .filter((m) => m.isActive)
        .sort((a, b) => a.machineNumber - b.machineNumber);
      // A prefilled machine (e.g. the order's scheduled machine) that isn't licensed for
      // shape punching isn't in the dropdown — drop it so the defaults below apply instead
      // of the select silently holding an invisible value.
      if (
        this.machineNumber != null &&
        this.machines.length &&
        !this.machines.some((m) => m.machineNumber === this.machineNumber)
      ) {
        this.machineNumber = null;
      }
      // One licensed machine on site: no choice to make, preselect it.
      if (this.machineNumber == null && this.machines.length === 1) {
        this.machineNumber = this.machines[0].machineNumber;
        this.filterToolings();
      }
      this.defaultMachineFromTooling();
    });

    this.materialsSub_ = this.clientDataStore.SelectCoilTypes().subscribe((materials) => {
      this.materials = materials ?? [];
      this.filterMaterials();
    });

    // Same lookup UX as the segment worksheet; free typing stays allowed (the field is
    // optional and server-side tooling code patterns can match codes not in this list).
    this.toolingService.getToolings().subscribe({
      next: (result) => {
        this.toolings = (result ?? []).sort((a, b) => a.toolingCode.localeCompare(b.toolingCode));
        this.filterToolings();
        this.defaultMachineFromTooling();
      },
      error: () => {}, // lookup unavailable — the field still accepts free typing
    });
  }

  /**
   * No machine picked yet but the tooling code matches a known tooling: default to the machine
   * of the last run if it runs that tooling, otherwise the tooling's first licensed machine.
   * Toolings with complex punching are usually assigned to exactly one machine, so this fills
   * the only field an order-line launch can't carry from the order.
   */
  defaultMachineFromTooling(): void {
    if (this.machineNumber != null || !this.machines.length) {
      return;
    }
    const code = this.toolingCode.trim().toLowerCase();
    if (!code) {
      return;
    }
    const tooling = this.toolings.find((t) => t.toolingCode?.toLowerCase() === code);
    const candidates = (tooling?.machines ?? [])
      .map((tm) => tm.machineNumber)
      .filter((n) => this.machines.some((m) => m.machineNumber === n));
    const machine =
      this.lastMachine != null && candidates.includes(this.lastMachine)
        ? this.lastMachine
        : candidates[0];
    if (machine != null) {
      this.machineNumber = machine;
      this.filterToolings();
    }
  }

  ngOnDestroy(): void {
    this.machinesSub_.unsubscribe();
    this.materialsSub_.unsubscribe();
  }

  /** Narrowed to the selected machine's toolings when a machine is chosen. */
  filterToolings(): void {
    const search = this.toolingCode.trim().toLowerCase();
    this.filteredToolings = this.toolings.filter(
      (t) =>
        (this.machineNumber == null ||
          t.machines?.some((m) => m.machineNumber === this.machineNumber)) &&
        (!search ||
          t.toolingCode.toLowerCase().includes(search) ||
          t.description?.toLowerCase().includes(search))
    );
  }

  filterMaterials(): void {
    const search = this.materialText.trim().toLowerCase();
    const matches = !search
      ? this.materials
      : this.materials.filter(
          (m) =>
            m.materialCode?.toLowerCase().includes(search) ||
            m.description?.toLowerCase().includes(search)
        );
    this.filteredMaterials = matches.slice(0, MAX_MATERIAL_OPTIONS);
  }

  /** The typed text must resolve to a real material — it supplies the gauge and width. */
  get selectedMaterial(): IMaterialDto | null {
    const code = this.materialText.trim().toLowerCase();
    if (!code) {
      return null;
    }
    return this.materials.find((m) => m.materialCode?.toLowerCase() === code) ?? null;
  }

  get materialHint(): string {
    const mat = this.selectedMaterial;
    if (!mat) {
      return this.materialText.trim() ? 'Not a known material' : '';
    }
    const gauge = mat.gauge ? `${mat.gauge} ga` : 'no gauge';
    const width = mat.widthIn ? this.unitsService.formatUserUnits(mat.widthIn, 'in', 3) : 'no width';
    return `${gauge} · ${width} wide`;
  }

  get parsedLengthIn(): number {
    return this.unitsService.parseLengthEquationToInches(this.lengthText);
  }

  get lengthHint(): string {
    if (!this.lengthText.trim()) {
      return `e.g. 120, 10' 6", 3000mm`;
    }
    const parsed = this.parsedLengthIn;
    if (!isFinite(parsed) || parsed <= 0) {
      return 'Not a valid length';
    }
    return `= ${this.unitsService.formatUserUnits(parsed, 'in', 3)}`;
  }

  get toolingRequired(): boolean {
    return this.data.macroKind === 'Placed';
  }

  get toolingHint(): string {
    return this.toolingRequired
      ? 'Required — a placed macro takes its Y from the profile'
      : 'Optional — sets axis positions, strip offset, and shape flips';
  }

  get canVisualize(): boolean {
    const parsed = this.parsedLengthIn;
    return (
      this.machineNumber != null &&
      this.selectedMaterial != null &&
      isFinite(parsed) &&
      parsed > 0 &&
      (!this.toolingRequired || !!this.toolingCode.trim())
    );
  }

  visualize(): void {
    if (!this.canVisualize) {
      return;
    }
    const material = this.selectedMaterial;
    writeLauncherMemory(
      {
        machineNumber: this.machineNumber,
        toolingCode: this.toolingCode,
        materialText: material.materialCode,
        lengthText: this.lengthText,
      },
      !!this.data.macroKind
    );
    // Same data contract as the solution-row path (punch-pattern-detail.openFlatView);
    // the launcher stays open underneath so the inputs survive for another run.
    this.dialog.open(FlatViewVisualizerComponent, {
      width: '96vw',
      maxWidth: '96vw',
      data: {
        patternId: this.data.patternId,
        patternName: this.data.patternName,
        machineNumber: this.machineNumber,
        toolingCode: this.toolingCode.trim() || null,
        gauge: material.gauge || null,
        lengthIn: this.parsedLengthIn,
        widthIn: material.widthIn,
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }
}
